import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Backdrop,
  Badge,
} from "@mui/material";
import { Close, Menu as MenuIcon, ShoppingCart } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import { useQuote } from "../context/QuoteContext";
import logo from "../assets/logo.png";

const StyledButton = styled(Button)(({ theme, active }) => ({
  color: active ? theme.palette.secondary.main : theme.palette.primary.main,
  fontSize: "1rem",
  textTransform: "none",
  position: "relative",
  fontWeight: active ? 700 : 500,
  padding: "0.5rem 1rem",
  "&:after": {
    content: '""',
    position: "absolute",
    width: active ? "100%" : "0%",
    height: "2px",
    bottom: "-3px",
    left: "0",
    backgroundColor: theme.palette.secondary.main,
    transition: "width 0.3s ease-in-out",
  },
  "&:hover:after": {
    width: "100%",
  },
}));

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const location = useLocation();
  const { quoteItems } = useQuote();

  const navItems = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Products", path: "/products" },
    { label: "Get Quote", path: "/inquiry" },
    { label: "Contact", path: "/contact" },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const addGoogleTranslateScript = () => {
      if (!document.querySelector("#google-translate-script")) {
        const script = document.createElement("script");
        script.id = "google-translate-script";
        script.src =
          "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        script.async = true;
        document.body.appendChild(script);
      }
    };

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { 
          pageLanguage: "en",
          includedLanguages: 'hi,mr,gu,pa,bn,ta,te,kn,ml,or,as,ur',
          autoDisplay: false 
        },
        "google_translate_element"
      );
    };

    addGoogleTranslateScript();
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: scrolled ? "rgba(255, 255, 255, 0.98)" : "white",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.1)" : "0 2px 10px rgba(0,0,0,0.05)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
          {/* Logo and Brand Name */}
          {/* Logo and Brand Name */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <img
              src={logo}
              alt="Company Logo"
              style={{ height: "70px", width: "auto" }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
              }}
            >
              <Typography
                variant="brandTitle"
                translate="no"
                sx={{
                  fontFamily: "'Cinzel', serif",
                  fontWeight: "bold",
                  letterSpacing: "7px",
                  fontSize: "2rem",
                  marginTop: "-0.5rem",
                }}
              >
                Shree Balaji
              </Typography>
              <Typography
                variant="brandTitle"
                translate="no"
                sx={{
                  fontFamily: "'Cinzel', serif",
                  color: theme.palette.secondary.main,
                  fontWeight: "bold",
                  // marginTop: "1rem",
                }}
              >
                Corporation
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
            {navItems.map((item) => (
              <StyledButton
                key={item.path}
                component={Link}
                to={item.path}
                active={location.pathname === item.path ? 1 : 0}
              >
                {item.label}
              </StyledButton>
            ))}
            
            {/* Quote Badge */}
            <IconButton
              component={Link}
              to="/inquiry"
              sx={{ ml: 2, color: theme.palette.primary.main }}
            >
              <Badge badgeContent={quoteItems.length} color="secondary">
                <ShoppingCart />
              </Badge>
            </IconButton>

            {/* Google Translate */}
            <Box
              id="google_translate_element"
              sx={{ 
                ml: 2,
                "& .goog-te-combo": {
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  fontSize: "14px",
                }
              }}
            />
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{ display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Add spacer to prevent content from hiding behind fixed navbar */}
      <Toolbar />

      {/* Backdrop Blur When Drawer is Open */}
      <Backdrop open={mobileOpen} onClick={handleDrawerToggle} sx={{ zIndex: 1200 }} />

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
            Shree Balaji Corporation
          </Typography>
          <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>

        {/* Drawer Menu Items */}
        <List>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  backgroundColor: isActive ? "rgba(255, 255, 255, 0.1)" : "transparent",
                  borderLeft: isActive ? "4px solid #FFC107" : "none",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    "& .MuiTypography-root": {
                      fontWeight: isActive ? 600 : 400,
                      color: "white",
                    },
                  }}
                />
                {item.label === "Get Quote" && quoteItems.length > 0 && (
                  <Badge badgeContent={quoteItems.length} color="secondary" />
                )}
              </ListItem>
            );
          })}
        </List>

        {/* Mobile Google Translate */}
        <Box sx={{ p: 2 }}>
          <div id="google_translate_element_mobile" />
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar;
